import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { useTerminalPasswordSaveMutation } from "src/app/features/TerminalPassword/services/server/TerminalPasswordApi";
import type { TerminalPasswordEntity } from "src/app/features/TerminalPassword/types/TerminalPasswordEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";

export const TerminalPasswordCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("terminal-password");

    const [formData, setFormData] = useState<TerminalPasswordEntity>({} as TerminalPasswordEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalPasswordSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            if (result?.data?.pinCode) {
                setFormData((prev) => ({ ...prev, pinCode: result.data.pinCode }));
            }

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("success.terminalPswd.create.successfully"),
                        autoCloseDisabled: false,
                    }),
                );
            }
        },
        [saveFormData, formData, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<TerminalPasswordEntity>
                    name="TerminalPasswordCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
